import {Plus, Trash2, ChevronDown, ChevronUp} from "lucide-react";
import { useState } from "react";
import apiFetch from "./../apiClient";

interface Category {
    id: string;
    name: string;
    type: "Débito" | "Crédito";
}

export function CategoryCard({ categories, onCategoriesChanged }) {
    const [expanded, setExpanded] = useState(false);
    const [categoryForm, setCategoryForm] = useState({ name: '', type: '', metaValor: '' });
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [metaEditCategoryId, setMetaEditCategoryId] = useState('');
    const [metaEditValue, setMetaEditValue] = useState('');
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const addCategory = async () => {
        if (!categoryForm.name.trim() || !categoryForm.type) {
            setError('Preencha o tipo e o nome da categoria.');
            return;
        }
        const newCategory: Category = {
            id: Date.now().toString(),
            name: categoryForm.name,
            type: categoryForm.type,
            meta_valor: categoryForm.metaValor ? Number(categoryForm.metaValor) : null,
        };
        setIsProcessing(true);
        setError(null);
        try {
            await apiFetch("/api/financecontrol/create-category", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newCategory),
            });
            setCategoryForm({ name: '', type: '', metaValor: '' });
            await onCategoriesChanged?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };
    const deleteCategory = async () => {
        if (!selectedCategoryId) return;
        setIsProcessing(true);
        setError(null);
        try {
            await apiFetch(`/api/financecontrol/delete-category/${selectedCategoryId}`, {
                method: "DELETE",
            });
            setSelectedCategoryId('');
            await onCategoriesChanged?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };
    const selectMetaEditCategory = (categoryId) => {
        setMetaEditCategoryId(categoryId);
        const category = categories && categories.find((c) => String(c.categoria_id) === String(categoryId));
        setMetaEditValue(category && category.categoria_meta_valor != null ? String(category.categoria_meta_valor) : '');
    };
    const saveCategoryMeta = async () => {
        if (!metaEditCategoryId) return;
        setIsProcessing(true);
        setError(null);
        try {
            await apiFetch(`/api/financecontrol/update-category-meta/${metaEditCategoryId}`, {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ meta_valor: metaEditValue ? Number(metaEditValue) : null }),
            });
            setMetaEditCategoryId('');
            setMetaEditValue('');
            await onCategoriesChanged?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };
    return (
        <div className="col-12 col-md-6">
            <div className="d-flex justify-content-end">
                <button
                    className="btn btn-outline-custom d-flex align-items-center gap-2"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                >
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    Categorias
                </button>
            </div>
            {expanded && (
            <div className="mt-2">
            <div className="d-flex gap-2">
                <label htmlFor="new-category-type" className="visually-hidden">Tipo de categoria</label>
                <select
                    id="new-category-type"
                    className="form-select form-select-custom"
                    value={categoryForm.type}
                    onChange={(e) => setCategoryForm({ ...categoryForm, type: e.target.value})}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                >
                    <option value="">Categoria</option>
                    <option value="debito">Débito</option>
                    <option value="credito">Crédito</option>
                    <option value="investimento">Investimento</option>
                </select>
                <input
                    type="text"
                    className="form-control form-select-custom"
                    placeholder="Nome da nova categoria..."
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
                <label htmlFor="new-category-meta" className="visually-hidden">Meta mensal (R$)</label>
                <input
                    id="new-category-meta"
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control form-select-custom"
                    style={{maxWidth: '140px'}}
                    placeholder="Meta mensal (R$)"
                    value={categoryForm.metaValor}
                    onChange={(e) => setCategoryForm({...categoryForm, metaValor: e.target.value})}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
                <button className="btn btn-primary-custom d-flex align-items-center gap-2" onClick={addCategory} disabled={isProcessing}>
                    <Plus size={16} /> Criar
                </button>
            </div>
            <div className="d-flex gap-2 mt-2">
                <label htmlFor="delete-category-select" className="visually-hidden">Excluir categoria</label>
                <select
                    id="delete-category-select"
                    className="form-select form-select-custom"
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                    <option value="">Excluir categoria...</option>
                    {categories && categories.map((category) => (
                        <option key={category.categoria_id} value={category.categoria_id}>
                            {category.categoria_nome} ({category.categoria_tipo})
                        </option>
                    ))}
                </select>
                <button
                    className="btn btn-sm d-flex align-items-center gap-2"
                    onClick={deleteCategory}
                    disabled={!selectedCategoryId || isProcessing}
                    style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--color-danger)',
                        color: 'var(--color-danger)'
                    }}
                    aria-label="Excluir categoria"
                >
                    <Trash2 size={14} />
                </button>
            </div>
            <div className="d-flex gap-2 mt-2">
                <label htmlFor="meta-edit-category-select" className="visually-hidden">Editar meta de categoria</label>
                <select
                    id="meta-edit-category-select"
                    className="form-select form-select-custom"
                    value={metaEditCategoryId}
                    onChange={(e) => selectMetaEditCategory(e.target.value)}
                >
                    <option value="">Editar meta de categoria...</option>
                    {categories && categories.map((category) => (
                        <option key={category.categoria_id} value={category.categoria_id}>
                            {category.categoria_nome} ({category.categoria_tipo})
                        </option>
                    ))}
                </select>
                <label htmlFor="meta-edit-value" className="visually-hidden">Nova meta mensal (R$)</label>
                <input
                    id="meta-edit-value"
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control form-select-custom"
                    style={{maxWidth: '140px'}}
                    placeholder="Meta mensal (R$)"
                    value={metaEditValue}
                    disabled={!metaEditCategoryId}
                    onChange={(e) => setMetaEditValue(e.target.value)}
                />
                <button
                    className="btn btn-sm btn-outline-custom"
                    onClick={saveCategoryMeta}
                    disabled={!metaEditCategoryId || isProcessing}
                >
                    Salvar
                </button>
            </div>
            {error && (
                <small className="text-danger-custom d-block mt-1">{error}</small>
            )}
            </div>
            )}
        </div>
    );
}